import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ReactFlowProvider } from 'reactflow';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Box } from '@mui/material';
import { AxiosError, type AxiosResponse } from 'axios';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { log } from '@/logger/logger';
import Flow from '@/components/Recipe/Flow';
import { FlowProvider } from '@/components/Recipe/FlowContext';
import { getAxiosInstance } from '@/services/axiosConfig';
import { MediaGalleryProvider } from '@/components/Recipe/FlowComponents/MediaGalleryContext';
import { ConnectionProvider } from '@/components/Recipe/FlowComponents/FlowTour/ConnectionContext';
import { WasmProvider } from '@/components/Recipe/WasmContext';
import { useQueryParamsContext } from '@/contexts/QueryParamsContext';
import { useIsFigmaEmbedMode } from '@/contexts/FigmaEmbedModeContext';
import PreventWheelEvents from '@/components/Common/PreventWheelEvents';
import { useIsWorkflowLoading, useWorkflowStore } from '@/state/workflow.state';
import { NoRecipeAccessError } from '@/components/Common/Error/NoRecipeAccessError';
import { useDisableZoom } from '@/UI/AppContextMenu/useDisableZoom';
import { Flex } from '@/UI/styles';
import { PageLoader } from '@/components/Common/PageLoader/PageLoader';
import { color } from '@/colors';
import { SocketProvider } from '@/hooks/useSocket';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_WORKSPACE_CUSTOM_FONTS } from '@/consts/featureFlags';
import { collectMissingWorkspaceFontFamiliesFromNodes } from '@/utils/customFonts.utils';
import { FlowRunProvider } from '@/components/Recipe/RunFlow/FlowRunContext';
import { useUpdateBatches } from '@/components/Recipe/RunFlow/batches.store';
import { useCanvasChromeStore } from '@/state/canvasChrome.state';
import { ReloadAlert } from '@/UI/ReloadAlert/ReloadAlert';
import { WorkflowDebugInfo } from '@/components/Recipe/WorkflowDebugInfo';
import { I18N_KEYS } from '@/language/keys';
import { useCustomFontsInfo } from '@/hooks/useCustomFontsInfo';
import type { Edge, Node } from '@/types/node';
import type { User } from '@/types/auth.types';
import type { Recipe } from '@/types/api/recipe';
import type { BatchRun } from '@/types/batch.types';

const logger = log.getLogger('Recipe');
const axiosInstance = getAxiosInstance();

function Recipe({ user }: { user: User | null }) {
  const params = useParams();
  const { queryParamVersion } = useQueryParamsContext();
  const recipeId = params.recipeId;
  const navigate = useNavigate();
  // In Figma embed mode, workflowRole is owned by `EmbedWorkflow` (derived from the JWT's
  // `permission` claim). Recipe's owner-based role logic would otherwise overwrite it to
  // `'guest'` every time the fetch completes, because the synthetic UID (`figma:<sub>`) never
  // matches `recipe.owner`.
  //
  // Skipping the block also skips `setViewingVersionMode`/`setIsLatestVersion`, which is fine
  // for V1: those fire only when `?version=` is in the URL (a deprecated standalone feature),
  // and Figma never passes query params to `/embed/flow/:recipeId`. The defaults
  // (`viewingVersionMode = false`, `isLatestVersion = true`) already describe the correct
  // embed behavior. If version viewing is brought back for embed later, this guard will need
  // to be revisited so the version branch still runs.
  const isFigmaEmbedMode = useIsFigmaEmbedMode();

  const [loadedNodes, setLoadedNodes] = useState<Node[]>([]);
  const [loadedEdges, setLoadedEdges] = useState<Edge[]>([]);
  const isLoadingRecipeRef = useRef(false);
  const [isUnauthorized, setIsUnauthorized] = useState(false);

  useDisableZoom();

  /// permissions
  const [viewingVersionMode, setViewingVersionMode] = useState(false);

  // Workflow store
  const setIsLatestVersion = useWorkflowStore((state) => state.setIsLatestVersion);
  const setWorkflowRole = useWorkflowStore((state) => state.setWorkflowRole);
  const setRecipe = useWorkflowStore((state) => state.setRecipe);
  const workflowRole = useWorkflowStore((state) => state.workflowRole);
  const recipeData = useWorkflowStore((state) => state.recipe);
  const { workflowCustomFontsUrlsMap, workspaceCustomFontFaceKeys, workspaceFontStyleCombinationsByFamily } =
    useCustomFontsInfo(recipeId);
  const { value: isWorkspaceCustomFontsEnabled } = useFeatureFlag(FF_WORKSPACE_CUSTOM_FONTS);
  const recipeLoadingState = useWorkflowStore((state) => state.loadingStates.get('recipe-version'));
  const workspaceFontsLoadingState = useWorkflowStore((state) => state.loadingStates.get('workspace-fonts'));
  const setLoadingState = useWorkflowStore((state) => state.setLoadingState);
  const loadNodeTypes = useWorkflowStore((state) => state.loadNodeTypes);
  const loadPrices = useWorkflowStore((state) => state.loadPrices);
  const loadWorkspaceFalByokStatus = useWorkflowStore((state) => state.loadWorkspaceFalByokStatus);
  const nodeTypesLoadingState = useWorkflowStore((state) => state.loadingStates.get('node-types'));
  const modelPricesLoadingState = useWorkflowStore((state) => state.loadingStates.get('model-prices'));
  const { t } = useTranslation();

  const missingWorkflowFontFamilies = useMemo(() => {
    if (isWorkspaceCustomFontsEnabled !== true || workspaceFontsLoadingState !== 'loaded') {
      return [];
    }
    return collectMissingWorkspaceFontFamiliesFromNodes(loadedNodes, workspaceCustomFontFaceKeys);
  }, [isWorkspaceCustomFontsEnabled, loadedNodes, workspaceCustomFontFaceKeys, workspaceFontsLoadingState]);

  const [missingWorkflowFontsModalDismissed, setMissingWorkflowFontsModalDismissed] = useState(false);

  const showMissingWorkflowFontsModal =
    workflowRole === 'editor' && missingWorkflowFontFamilies.length > 0 && !missingWorkflowFontsModalDismissed;

  const handleMissingWorkspaceFontsReplaced = useCallback(() => {
    setMissingWorkflowFontsModalDismissed(true);
  }, []);

  // Batches store
  const setBatchRuns = useUpdateBatches();

  const setIsLoadingRecipe = useCallback(
    (isLoading: boolean) => {
      isLoadingRecipeRef.current = isLoading;
      setLoadingState('recipe-version', isLoading ? 'loading' : 'loaded');
    },
    [setLoadingState],
  );

  const getRecipe = useCallback(
    async (recipeIdToFetch: string) => {
      try {
        if (isLoadingRecipeRef.current) {
          return;
        }
        setIsLoadingRecipe(true);
        const version = queryParamVersion;
        let recipe: Recipe;
        let response: AxiosResponse<Recipe>;
        try {
          response = await axiosInstance.get<Recipe>(
            `/v1/recipes/${recipeIdToFetch}${version !== undefined ? `?version=${version}` : ''}`,
          );
          recipe = response.data;
        } catch (error) {
          if (error instanceof AxiosError && error.response && error.response.status === 403) {
            // In embed mode, `user` is expected to be null (no /v1/users enrichment for embed
            // sessions yet), and /signin is both outside `/embed/*` and blocked by the embed
            // CSP frame-ancestors. Show the access error in place instead.
            if (!user && !isFigmaEmbedMode) {
              navigate(`/signin`, { state: { from: window.location.pathname } });
              return;
            }
            setIsUnauthorized(true);
            setIsLoadingRecipe(false);
            return;
          }
          throw error;
        }
        if (!recipe) {
          throw new Error('Recipe returned empty response', {
            cause: {
              recipeResponse: response,
              recipeId: recipeIdToFetch,
              version,
            },
          });
        }
        setLoadedNodes(recipe.nodes || []);
        setLoadedEdges(recipe.edges || []);
        if (recipe.batchRuns) {
          setBatchRuns(
            recipe.batchRuns.reduce(
              (acc, batchRun) => {
                acc[batchRun.batchId] = batchRun;
                return acc;
              },
              {} as Record<string, BatchRun>,
            ),
          );
        }
        setIsLoadingRecipe(false);

        /// setting permissions
        if (!isFigmaEmbedMode) {
          setWorkflowRole('guest');
          if (user && recipe.owner === user.uid) {
            if (version !== undefined && version !== recipe.latestVersion.version) {
              setViewingVersionMode(true); // deprecated
              setIsLatestVersion(false);
            } else {
              setWorkflowRole('editor');
            }
          }
        }

        setRecipe({
          id: recipeIdToFetch,
          folderId: recipe.folderId,
          scope: recipe.scope,
          name: recipe.name,
          poster: recipe.poster,
          visibility: recipe.visibility,
          publishedVersions: recipe.publishedVersions,
          latestPublishedVersion: recipe.latestPublishedVersion,
          version: recipe.version,
          designAppMetadata: recipe.designAppMetadata,
          updatedAt: recipe.updatedAt,
          v3: recipe.v3,
          sections: recipe.sections,
          isDeleted: recipe.isDeleted,
          workspaceId: recipe.workspaceId,
          owner: recipe.owner,
        });
      } catch (error) {
        logger.error('Error getting flows', error);
        setIsLoadingRecipe(false);
        if (error instanceof AxiosError && error.response && [404, 403].includes(error.response.status)) {
          // In embed mode the app is mounted under `/embed/*`. Navigating away from that prefix
          // switches `useAuthConfig` from FIGMA_EMBED to FIREBASE, and the AuthProvider subtree
          // would need to swap auth hooks mid-render. Show the access error in place instead.
          if (isFigmaEmbedMode) {
            setIsUnauthorized(true);
          } else {
            navigate('/');
          }
        }
      }
    },
    [
      isFigmaEmbedMode,
      navigate,
      queryParamVersion,
      setBatchRuns,
      setIsLatestVersion,
      setIsLoadingRecipe,
      setRecipe,
      setWorkflowRole,
      user,
    ],
  );

  useEffect(() => {
    if (recipeId && !isLoadingRecipeRef.current) {
      void getRecipe(recipeId);
    }
  }, [getRecipe, recipeId]);

  useEffect(() => {
    if (user && nodeTypesLoadingState === 'initial') {
      void loadNodeTypes();
    }
  }, [loadNodeTypes, nodeTypesLoadingState, user]);

  useEffect(() => {
    if (user && modelPricesLoadingState === 'initial') {
      void loadPrices();
    }
  }, [loadPrices, modelPricesLoadingState, user]);

  useEffect(() => {
    if (user && recipeData.workspaceId) {
      void loadWorkspaceFalByokStatus(recipeData.workspaceId);
    }
  }, [user, recipeData.workspaceId, loadWorkspaceFalByokStatus]);

  // Update page title when recipe loads and restore when component unmounts
  useEffect(() => {
    if (recipeData?.name) {
      document.title = recipeData.name;
    }

    return () => {
      document.title = t(I18N_KEYS.GENERAL.PAGE_TITLE);
    };
  }, [recipeData?.name, t]);

  if (isUnauthorized) {
    return <NoRecipeAccessError />;
  }

  const workspaceFontsBlockShell = workspaceFontsLoadingState !== 'loaded' && workspaceFontsLoadingState !== 'error';

  if (
    recipeLoadingState !== 'loaded' ||
    (user && (nodeTypesLoadingState !== 'loaded' || modelPricesLoadingState !== 'loaded')) ||
    workspaceFontsBlockShell
  ) {
    return null;
  }

  if (!recipeId) {
    return <Navigate to="/" />;
  }

  return (
    <>
      <WasmProvider>
        <FlowProvider nodes={loadedNodes} edges={loadedEdges} workflowCustomFontsUrlsMap={workflowCustomFontsUrlsMap}>
          <MediaGalleryProvider>
            <ReactFlowProvider>
              <ConnectionProvider>
                <SocketProvider>
                  <FlowRunProvider recipeId={recipeId}>
                    <Flow
                      recipeId={recipeId}
                      user={user}
                      viewingVersionMode={viewingVersionMode}
                      missingWorkflowFontsModalInfo={
                        showMissingWorkflowFontsModal
                          ? {
                              open: true,
                              families: missingWorkflowFontFamilies,
                              onReplaceComplete: handleMissingWorkspaceFontsReplaced,
                              workspaceFontStyleCombinationsByFamily,
                            }
                          : undefined
                      }
                    />
                  </FlowRunProvider>
                </SocketProvider>
              </ConnectionProvider>
            </ReactFlowProvider>
          </MediaGalleryProvider>
        </FlowProvider>
      </WasmProvider>
      <ReloadAlert />
    </>
  );
}

const WrappedRecipe = ({ user }: { user: User | null }) => {
  const isWorkflowLoading = useIsWorkflowLoading();
  const hasSelectedNodes = useCanvasChromeStore((s) => s.hasSelectedNodes);
  return (
    <PreventWheelEvents sx={{ display: 'flex', flexDirection: 'row', width: '100%', height: '100%' }}>
      <Flex sx={{ width: '100%', height: '100%' }}>
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'relative',
            backgroundColor: color.Black100,
            overflow: 'hidden',
          }}
          data-has-selected-nodes={hasSelectedNodes ? 'true' : 'false'}
          id="canvas"
        >
          <Recipe user={user} />
          <WorkflowDebugInfo />
          <AnimatePresence>
            {isWorkflowLoading && (
              <motion.div
                key="recipe-page-loader"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <PageLoader />
              </motion.div>
            )}
          </AnimatePresence>
        </Box>
      </Flex>
    </PreventWheelEvents>
  );
};

export { WrappedRecipe as Recipe };
